import React, { FC, useState } from 'react'
import styled, { css } from 'styled-components'
import { useDebouncedCallback } from 'use-debounce'

import { ButtonStyled, Text, Title } from '../CssStyled'
import InfinityObserver from 'components/Observer/InfinityObserver'
import { ISection } from '../../interfaces'
import { useAdsForm } from '@ads/front-core/hooks/useAdsForm'
import TextInput from '@ads/front-core/components/Form/TextInput'
import { IForm, joiSchema } from './formConfig'
import { OtherService } from '@ads/front-core/api'
import { Trans, lng } from '@ads/front-core/locales'
import TextArea from '@ads/front-core/components/Form/TextArea'
import { breakpoints } from '@ads/front-core/constants/breakpoints'

const Contact: FC<ISection> = ({ setActiveMenu }) => {
  const [isSent, setIsSent] = useState(false)

  const setIsSentDebounced = useDebouncedCallback(setIsSent, 3000)

  const {
    control,
    formState: { errors },
    isSubmitting,
    handleSubmit,
  } = useAdsForm<IForm>({
    joiSchema,
    onSubmit: async data => {
      await OtherService.sendContact({
        requestBody: {
          email: data.email,
          message: data.message,
          phone: data.phone,
          subject: lng('landing:newCustomerNotification'),
          to: 'SALES',
          source: 'LANDING_PAGE_FORM',
        },
      })
      setIsSent(true)
      setIsSentDebounced(false)
    },
  })

  return (
    <InfinityObserver callback={() => setActiveMenu('JoinUs')}>
      <Container>
        <Form>
          <Title color="inverted100">
            <Trans
              i18nKey="landing:doYouHaveAnyQuestions"
              components={{
                bold: <Title bold color="inverted100" />,
              }}
            />
          </Title>

          <TextToUs>
            <Trans
              i18nKey="landing:contactWithUs"
              components={{
                text: <Text color="inverted100" />,
              }}
            />
          </TextToUs>

          <Content>
            <TextInputStyled
              control={control}
              name="email"
              error={errors?.email}
              placeholder="Email"
            />
            <TextInputStyled
              control={control}
              name="phone"
              error={errors?.phone}
              placeholder={lng('landing:phone')}
            />
            <TextAreaStyled
              control={control}
              name="message"
              error={errors?.message}
              placeholder={lng('landing:writeMessage')}
            />

            <SendButton
              onClick={handleSubmit}
              isExpanded={isSent}
              disabled={isSubmitting}
            >
              <span>
                {lng(isSent ? 'common:messageWasSent' : 'common:send')}
              </span>
            </SendButton>
          </Content>
        </Form>

        <ImgWrapper></ImgWrapper>
      </Container>
    </InfinityObserver>
  )
}

const Container = styled.div`
  position: relative;
  display: flex;
  margin: 0 -60px 0 -60px;
  padding-left: 60px;

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    right: 50%;
    width: 100vw;
    margin-left: -50vw;
    margin-right: -50vw;
    bottom: 0;
    top: 0;
    background-color: #262a44;
    z-index: -1;
  }

  @media (max-width: ${breakpoints.MOBILE}) {
    flex-direction: column;
    margin: 0;
    padding-left: 0;
  }
`

const Form = styled.div`
  flex: none;
  width: 556px;
  margin-right: 16px;
  padding: 60px 0;

  @media (max-width: ${breakpoints.MOBILE}) {
    width: 100%;
    padding: 24px 0;
  }
`

const TextToUs = styled.div`
  margin: 24px 0;
`

const TextInputStyled = styled(TextInput)`
  input {
    height: 55px;
    padding: 14px 36px;
    border: 2px solid #e6e8ed;
    border-radius: 50px;
    font-size: 18px;
  }
`

const TextAreaStyled = styled(TextArea)`
  textarea {
    height: 150px;
    padding: 14px 36px;
    border: 2px solid #e6e8ed;
    border-radius: 25px;
    font-size: 18px;
  }
`

const Content = styled.div`
  position: relative;
  display: flex;
  gap: 24px;
  flex-direction: column;
`

const ImgWrapper = styled.div`
  position: relative;
  width: 100%;
  background: url('/img/landing/img_3.png');
  background-repeat: no-repeat;
  background-size: cover;

  &:after {
    content: '';
    position: absolute;
    left: -1px;
    top: 0;
    bottom: 0;
    right: 50%;
    background: linear-gradient(
      90deg,
      rgb(38, 42, 68) 0%,
      rgba(38, 42, 68, 0) 90.29%
    );
  }

  @media (max-width: ${breakpoints.MOBILE}) {
    height: 415px;
    background-position: top;
    margin: 0px -16px;
    width: auto;

    &:after {
      content: '';
      position: absolute;
      right: 0;
      background: linear-gradient(
        -180deg,
        rgb(38, 42, 68) 0%,
        rgba(38, 42, 68, 0) 90.29%
      );
    }
  }
`

const SendButton = styled.button<{ disabled?: boolean; isExpanded: boolean }>`
  ${ButtonStyled}

  ${p => css`
    cursor: ${p.disabled ? 'not-allowed' : 'pointer'};
    ${p.disabled &&
    css`
      opacity: 0.8;
    `}

    position: relative;
    align-self: flex-end;
    width: ${p.isExpanded ? 340 : 130}px;
    overflow: hidden;
    transition: 0.5s width, 0.3s opacity;
    justify-content: flex-start;

    &:after {
      position: absolute;
      background: linear-gradient(
        90deg,
        rgba(236, 28, 36, 0) 0%,
        rgb(236, 28, 36) 50%
      );
      content: '';
      right: 0px;
      width: 20px;
      top: 0;
      bottom: 0;
    }

    span {
      text-align: center;
      width: 100%;
    }
  `}
`

export default Contact
